import React, { useEffect, useState } from 'react'
import { Button, Form, Table } from 'react-bootstrap'
import { Checkbox, FormControlLabel } from '@material-ui/core'
import Select from 'react-select'
import AsyncSelect from 'components/Select/AsyncSelect'
import { RequireToggle } from 'components/RequireConfig'

import {
  fetchAddressDistricts,
  fetchAddressSubdistricts,
  searchHospitals,
  unwrapList,
} from './api'

const BANGKOK_PROVINCE_CODE = '10'

const isBangkokProvince = (provinceCode) =>
  String(provinceCode || '').trim() === BANGKOK_PROVINCE_CODE

export const Field = ({
  label,
  value,
  onChange,
  type = 'text',
  as,
  required,
  requireField,
  disabled,
  placeholder,
  min,
  max,
  rows = 3,
}) => (
  <Form.Group>
    <Form.Label>
      <RequireToggle field={requireField} />
      {label} {required && <span className="text-danger">*</span>}
    </Form.Label>
    <Form.Control
      as={as}
      type={type}
      rows={rows}
      value={value ?? ''}
      min={min}
      max={max}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </Form.Group>
)

export const ProjectCheckbox = ({
  label,
  checked,
  onChange,
  disabled,
  className,
  title,
  ariaLabel,
  onClick,
}) => {
  const control = (
    <Checkbox
      color="primary"
      checked={Boolean(checked)}
      disabled={disabled}
      title={title}
      inputProps={{ 'aria-label': ariaLabel || label || 'checkbox' }}
      onClick={onClick}
      onChange={(event) => onChange(event.target.checked)}
    />
  )

  if (!label) return <span className={className}>{control}</span>

  return (
    <FormControlLabel
      className={className}
      control={control}
      label={label}
    />
  )
}

export const CheckField = ({ label, checked, onChange, disabled }) => (
  <Form.Group className="pt-2">
    <ProjectCheckbox
      label={label}
      checked={Boolean(Number(checked)) || checked === true}
      disabled={disabled}
      onChange={(value) => onChange(value ? 1 : 0)}
    />
  </Form.Group>
)

export const SelectField = ({
  label,
  value,
  options,
  onChange,
  required,
  requireField,
  disabled,
  isClearable = true,
  placeholder = 'กรุณาเลือก',
}) => {
  const selected =
    options.find((option) => String(option.value) === String(value)) || null

  return (
    <Form.Group>
      <Form.Label>
        <RequireToggle field={requireField} />
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <Select
        classNamePrefix="patient-register-field-select"
        value={selected}
        options={options}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        isDisabled={disabled}
        isClearable={isClearable}
        placeholder={placeholder}
        noOptionsMessage={() => 'ไม่พบข้อมูล'}
        onChange={(option) =>
          onChange(option?.value ?? '', option?.record || null)
        }
      />
    </Form.Group>
  )
}

const toHospitalOption = (record) => {
  const value = record?.hospitalCode ?? record?.code ?? record?.id
  if (value === undefined || value === null || value === '') return null
  const name = record?.name || record?.hospitalName || record?.description || ''
  return {
    value,
    label: name ? `${value} : ${name}` : String(value),
    record,
  }
}

export const AsyncHospitalSelectField = ({
  label,
  value,
  onChange,
  required,
  requireField,
  disabled,
  placeholder = 'พิมพ์รหัสหรือชื่อโรงพยาบาล',
}) => {
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    let active = true
    const hospitalCode = String(value || '').trim()

    if (!hospitalCode) {
      setSelected(null)
      return () => {
        active = false
      }
    }

    setSelected({ value: hospitalCode, label: hospitalCode, record: null })
    searchHospitals(hospitalCode)
      .then((response) => {
        if (!active) return
        const option = unwrapList(response)
          .map(toHospitalOption)
          .filter(Boolean)
          .find((item) => String(item.value) === hospitalCode)
        if (option) setSelected(option)
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [value])

  const loadOptions = async (inputValue) => {
    const searchKey = String(inputValue || '').trim()
    if (searchKey.length < 2) return []
    const response = await searchHospitals(searchKey)
    return unwrapList(response).map(toHospitalOption).filter(Boolean)
  }

  return (
    <Form.Group>
      <Form.Label>
        <RequireToggle field={requireField} />
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <AsyncSelect
        classNamePrefix="patient-register-field-select"
        value={selected}
        cacheOptions
        defaultOptions={false}
        isDisabled={disabled}
        isClearable
        placeholder={placeholder}
        noOptionsMessage={({ inputValue }) =>
          String(inputValue || '').trim().length < 2
            ? 'พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหา'
            : 'ไม่พบโรงพยาบาล'
        }
        loadOptions={loadOptions}
        onChange={(option) => {
          setSelected(option || null)
          onChange(option?.value ?? '', option?.record || null)
        }}
      />
    </Form.Group>
  )
}

export const SectionTitle = ({ children, action }) => (
  <div className="d-flex align-items-center justify-content-between border-bottom mb-3 pb-2">
    <h5 className="mb-0">{children}</h5>
    {action}
  </div>
)

const toAddressOptions = (response, codeKey) =>
  unwrapList(response)
    .map((record) => {
      const value = record?.[codeKey] ?? record?.code ?? record?.id
      const name =
        record?.name || record?.description || record?.display || value
      if (value === undefined || value === null || value === '') return null
      return { value, label: `${value} - ${name}`, record }
    })
    .filter(Boolean)

const withCurrentAddressOption = (options, value, record) => {
  const formatted = (options || []).map((option) => ({
    ...option,
    label: String(option.label || '').startsWith(`${option.value} -`)
      ? option.label
      : `${option.value} - ${option.label}`,
  }))
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    formatted.some((option) => String(option.value) === String(value))
  ) {
    return formatted
  }

  const name = record?.name || record?.description || record?.display || ''
  return [
    {
      value,
      label: name ? `${value} - ${name}` : String(value),
      record,
    },
    ...formatted,
  ]
}

export const AddressForm = ({
  value,
  onChange,
  disabled,
  allowCopy,
  onCopy,
  options = {},
}) => {
  const [districtOptions, setDistrictOptions] = useState([])
  const [subDistrictOptions, setSubDistrictOptions] = useState([])

  useEffect(() => {
    let isCurrent = true
    setSubDistrictOptions([])

    if (!value.provinceCode) {
      setDistrictOptions([])
      return () => {
        isCurrent = false
      }
    }

    fetchAddressDistricts(value.provinceCode)
      .then((response) => {
        if (isCurrent) {
          setDistrictOptions(toAddressOptions(response, 'districtCode'))
        }
      })
      .catch(() => {
        if (isCurrent) setDistrictOptions([])
      })

    return () => {
      isCurrent = false
    }
  }, [value.provinceCode])

  useEffect(() => {
    let isCurrent = true

    if (!value.provinceCode || !value.districtCode) {
      setSubDistrictOptions([])
      return () => {
        isCurrent = false
      }
    }

    fetchAddressSubdistricts(value.provinceCode, value.districtCode)
      .then((response) => {
        if (isCurrent) {
          setSubDistrictOptions(toAddressOptions(response, 'subDistrictCode'))
        }
      })
      .catch(() => {
        if (isCurrent) setSubDistrictOptions([])
      })

    return () => {
      isCurrent = false
    }
  }, [value.districtCode, value.provinceCode])

  const set = (key, fieldValue) => onChange({ ...value, [key]: fieldValue })

  const setLocation = (
    key,
    fieldValue,
    relationKey,
    record,
    resetValues = {}
  ) => {
    onChange({
      ...value,
      ...resetValues,
      [key]: fieldValue,
      [relationKey]: record || null,
    })
  }

  const provinceOptions = withCurrentAddressOption(
    options.addressProvinces,
    value.provinceCode,
    value.addressProvince
  )
  const countryOptions = withCurrentAddressOption(
    options.addressCountries,
    value.countryCode,
    value.addressCountry
  )
  const availableDistricts = withCurrentAddressOption(
    districtOptions,
    value.districtCode,
    value.addressDistrict
  )
  const availableSubDistricts = withCurrentAddressOption(
    subDistrictOptions,
    value.subDistrictCode,
    value.addressSubdistrict
  )
  const isBangkokAddress = isBangkokProvince(value.provinceCode)
  const districtLabel = isBangkokAddress ? 'เขต' : 'อำเภอ'
  const subDistrictLabel = isBangkokAddress ? 'แขวง' : 'ตำบล'

  return (
    <React.Fragment>
      {allowCopy && (
        <div className="patient-register-address-copy">
          <CheckField
            label="ที่อยู่ตามบัตรประชาชน"
            checked={value.isSameCitizenAddress}
            disabled={disabled}
            onChange={(checked) => {
              set('isSameCitizenAddress', checked)
              if (checked) onCopy()
            }}
          />
        </div>
      )}

      <div className="patient-register-address-form-grid">
        <div className="patient-register-address-control span-4">
          <Field
            label="บ้านเลขที่"
            value={value.houseNumber}
            disabled={disabled}
            onChange={(next) => set('houseNumber', next)}
          />
        </div>
        <div className="patient-register-address-control span-2 is-compact">
          <Field
            label="หมู่"
            value={value.villageCode}
            disabled={disabled}
            onChange={(next) => set('villageCode', next)}
          />
        </div>
        <div className="patient-register-address-control span-6">
          <Field
            label="ซอย/ถนน"
            value={value.roadName}
            disabled={disabled}
            onChange={(next) => set('roadName', next)}
          />
        </div>

        <div className="patient-register-address-control span-6">
          <SelectField
            label="จังหวัด"
            value={value.provinceCode}
            options={provinceOptions}
            disabled={disabled}
            placeholder="รหัส - ชื่อจังหวัด"
            onChange={(next, record) =>
              setLocation('provinceCode', next, 'addressProvince', record, {
                districtCode: '',
                subDistrictCode: '',
                addressDistrict: null,
                addressSubdistrict: null,
              })
            }
          />
        </div>
        <div className="patient-register-address-control span-6">
          <SelectField
            label={districtLabel}
            value={value.districtCode}
            options={availableDistricts}
            disabled={disabled || !value.provinceCode}
            placeholder={`รหัส - ชื่อ${districtLabel}`}
            onChange={(next, record) =>
              setLocation('districtCode', next, 'addressDistrict', record, {
                subDistrictCode: '',
                addressSubdistrict: null,
              })
            }
          />
        </div>

        <div className="patient-register-address-control span-6">
          <SelectField
            label={subDistrictLabel}
            value={value.subDistrictCode}
            options={availableSubDistricts}
            disabled={disabled || !value.provinceCode || !value.districtCode}
            placeholder={`รหัส - ชื่อ${subDistrictLabel}`}
            onChange={(next, record) =>
              setLocation(
                'subDistrictCode',
                next,
                'addressSubdistrict',
                record,
                record
                  ? {
                      postalCode:
                        record.postalCode || record.zipCode || value.postalCode,
                    }
                  : {}
              )
            }
          />
        </div>
        <div className="patient-register-address-control span-6">
          <SelectField
            label="ประเทศ"
            value={value.countryCode}
            options={countryOptions}
            disabled={disabled}
            placeholder="รหัส - ชื่อประเทศ"
            onChange={(next, record) =>
              setLocation('countryCode', next, 'addressCountry', record)
            }
          />
        </div>

        <div className="patient-register-address-control span-6">
          <Field
            label="รหัสไปรษณีย์"
            value={value.postalCode}
            disabled={disabled}
            max="99999"
            onChange={(next) =>
              set('postalCode', String(next || '').slice(0, 5))
            }
          />
        </div>
        <div className="patient-register-address-control span-6">
          <Field
            label="เบอร์โทรศัพท์"
            value={value.phoneNumber}
            disabled={disabled}
            onChange={(next) => set('phoneNumber', next)}
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export const EditableTable = ({
  rows,
  columns,
  createRow,
  onChange,
  disabled,
  striped = false,
  tableClassName,
  addText = 'เพิ่มรายการ',
  emptyText = 'ยังไม่มีข้อมูล',
}) => {
  const updateRow = (index, key, value) => {
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [key]: value } : row
    )
    onChange(nextRows)
  }

  return (
    <React.Fragment>
      <div className="text-right mb-2">
        <Button
          size="sm"
          variant="outline-primary"
          disabled={disabled}
          title={addText}
          onClick={() => onChange([...rows, createRow()])}
        >
          <i className="feather icon-plus mr-1" />
          {addText}
        </Button>
      </div>
      <Table
        bordered
        responsive
        hover
        striped={striped}
        size="sm"
        className={tableClassName}
      >
        <thead>
          <tr>
            <th style={{ width: 50 }}>#</th>
            {columns.map((column) => (
              <th
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.label}
              </th>
            ))}
            <th style={{ width: 70 }} />
          </tr>
        </thead>
        <tbody>
          {!rows.length && (
            <tr>
              <td
                colSpan={columns.length + 2}
                className="text-center text-muted py-3"
              >
                {emptyText}
              </td>
            </tr>
          )}
          {rows.map((row, index) => (
            <tr key={row.id || row.uuid || `${index}`}>
              <td className="text-center align-middle">{index + 1}</td>
              {columns.map((column) => (
                <td
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                >
                  {column.render ? (
                    column.render({
                      row,
                      index,
                      value: row[column.key],
                      disabled,
                      onChange: (value) =>
                        updateRow(index, column.key, value),
                    })
                  ) : column.options ? (
                    <Select
                      value={
                        column.options.find(
                          (option) =>
                            String(option.value) === String(row[column.key])
                        ) || null
                      }
                      options={column.options}
                      isClearable
                      isDisabled={disabled}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      onChange={(option) =>
                        updateRow(index, column.key, option?.value ?? '')
                      }
                    />
                  ) : (
                    <Form.Control
                      size="sm"
                      type={column.type || 'text'}
                      value={row[column.key] ?? ''}
                      disabled={disabled}
                      placeholder={column.placeholder}
                      onChange={(event) =>
                        updateRow(index, column.key, event.target.value)
                      }
                    />
                  )}
                </td>
              ))}
              <td className="text-center align-middle">
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={disabled}
                  title="ลบรายการ"
                  onClick={() =>
                    onChange(rows.filter((_, rowIndex) => rowIndex !== index))
                  }
                >
                  <i className="feather icon-trash-2" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </React.Fragment>
  )
}
